/**
 * Thin domain-friendly wrappers over auto-generated OpenAPI SDK functions.
 *
 * Why:
 *  - Keep ergonomic names (createClient, listClients, etc.)
 *  - Centralize auth header building & future cross-cutting concerns (retry, logging)
 *  - Decouple the rest of the app from verbose generated identifiers so regeneration is painless
 *  - Provide stable return shapes matching existing manual API functions
 */

import {
  deleteAccountData,
  deleteFilesByFileId,
  deleteOrg,
  deleteOrgInvitesByInviteId,
  deleteOrgMembersByUserId,
  deletePasskeysByCredentialId,
  deleteSharesByShareId,
  deleteSupportThreadsByThreadIdMessagesByMessageIdAttachmentsByIndex,
  putSharesByShareIdExecutor,
  getFilesByFileIdDownload,
  getUnlockPetitionsPetitionDocKeyByClientId,
  getUnlockPetitionsByPetitionIdCertificate,
  getAdminFirms,
  getAuthMe,
  getClientAccessList,
  getClientByClientIdVaultDetails,
  getClientGet,
  getClientList,
  getFilesList,
  getHealth,
  getOrgBranding as getOrgBrandingApi,
  getOrgInvites,
  getOrgInvitesPreviewByToken,
  getOrgList,
  getOrgMembers,
  getOrgMembersByUserId,
  getPasskeys,
  getSharesByTrusteeByTrusteeIdentifier,
  getStripeCheckSubscriptionStatus,
  getStripeInvoices,
  getSupportThreads,
  getSupportThreadsByThreadId,
  getUser as getUserApi,
  getUserInvites as getUserInvitesApi,
  getUserTotpStatus,
  patchOrgBranding as patchOrgBrandingApi,
  patchOrgMembersByUserIdRole,
  patchOrgName,
  patchUser,
  postAuthPasskeyLoginFinish,
  postAuthPasskeyLoginStart,
  postAuthRevokeSessions,
  postAuthSendMagicLink,
  postAuthVerifyMagicLink,
  postAuthVerifyTotp,
  postClientAccessGrant,
  postClientAccessRevoke,
  postClientAuthLoginFinish,
  postClientAuthLoginStart,
  postClientAuthRegisterFinish,
  postClientAuthRegisterStart,
  postClientByClientIdAttestation,
  postClientCheckOwnership,
  postClientCreate,
  postClientResetVault,
  postVaultProfileName,
  postClientUpdate,
  postRecoveryResetVault,
  getRecoveryMyVaults,
  postFilesByFileIdUpload,
  postFilesInitiate,
  postOrg,
  postOrgEscrowKey as postOrgEscrowKeyApi,
  postOrgInvitesAccept,
  postOrgInvites,
  postOrgInvitesByInviteIdAccept,
  postOrgInvitesByInviteIdReject,
  postOrgInvitesByInviteIdResend,
  postPasskeysRegisterFinish,
  postPasskeysRegisterStart,
  postShares,
  postUnlockConfig,
  getUnlockConfig,
  getUnlockAdviserKey,
  postUnlockStart,
  getUnlockBySessionId,
  postUnlockBySessionIdPartial,
  getUnlockBySessionIdPartials,
  postStripeConfirmSubscription,
  postStripeCreateSubscriptionIntent,
  postStripeCustomerPortal,
  postSupportThreadsByThreadIdClose,
  postUserConfirmEmailChange,
  postUserConfirmTotpSetup,
  postUserDisableTotp,
  postUserRequestEmailChange,
  postUserSetupTotp,
  postAuthTrusteeSendLink,
  postAuthTrusteeVerifyLink,
  postIdentityVerifyStart,
  getIdentityVerifyStatus,
} from "./client";

import type {
  AcceptInviteResponse,
  InvitePreviewResponse,
  AuthMeResponse,
  PetitionDocKeyResponse,
  ClientDoc,
  ClientVaultDetails,
  ConfirmEmailChangeBody,
  CreateAttestationRequest,
  CreateAttestationResponse,
  CreateClientRequest,
  CreateShareRequest,
  CreateShareResponse,
  FileListItem,
  GetBrandingResponse,
  InitiateFileRequest,
  OwnedVaultItem,
  ResetVaultResponse,
  InitiateFileResponse,
  InvoicesResponse,
  LoginFinishResponse,
  LoginInitResponse,
  MessageDoc,
  OrgInvitation,
  OrgListEntry,
  OrgMemberWithEmail,
  PasskeyChallengeResponse,
  PasskeyListResponse,
  RegisterFinishResponse,
  RegisterInitResponse,
  RequestEmailChangeBody,
  RequestEmailChangeResponse,
  SetupTotpResponse,
  SetVaultConfigRequest,
  GetVaultConfigResponse,
  ShareItem,
  StartUnlockRequest,
  StartUnlockResponse,
  SubmitPartialRequest,
  SubmitPartialResponse,
  ListPartialsResponse,
  UnlockStatusResponse,
  ThreadDetailResponse,
  ThreadsListResponse,
  TotpStatusResponse,
  UpdateBrandingRequest,
  UpdateClientRequest,
  UpdateOwnNameRequest,
  UpdateUserRequest,
  UploadResponse,
  User,
  UserInviteItem,
  UserInviteListResponse,
  VerifyMagicLinkResponse,
  VerifyTotpSuccessResponse,
  VerifyTrusteeLinkResponse,
  StartVerificationRequest,
  StartVerificationResponse,
  StatusResponse as IdentityStatusResponse,
} from "./client";

// Re-exported so consumers can `import { type OwnedVaultItem } from "@secure-vault/api"`
// (used by the vault app's owner-reset flow).
export type {
  OwnedVaultItem,
  ResetVaultResponse,
  UpdateOwnNameRequest,
} from "./client";

// Trustee identity-verification (KYC) types. `StatusResponse` is re-exported under
// a disambiguated name since the generated identifier is generic.
export type {
  StartVerificationRequest,
  StartVerificationResponse,
} from "./client";
export type { StatusResponse as IdentityStatusResponse } from "./client";

/** Verification purpose: one-time seat bind vs. per-unlock-session step-up. */
export type IdentityVerificationPurpose = "nomination" | "unlock";

export type CreateOrgResponse = {
  org_id: string;
  name: string;
};

import { getHttpClient, withAuthHeaders } from "./runtime";
import { getVaultSessionToken } from "./runtime";

async function withVaultHeaders() {
  const token = getVaultSessionToken();
  return withAuthHeaders(
    token ? { Authorization: `Bearer ${token}` } : undefined
  );
}

// Unified execution helper to reduce repetition
type SdkResult<T> = {
  data?: T;
  error?: unknown;
  response?: { status: number };
};

async function exec<T>(fn: Promise<SdkResult<T>>): Promise<T> {
  const { data, error, response } = await fn;
  if (error) {
    if (response?.status && typeof error === "object" && error !== null) {
      (error as any).status = response.status;
    }
    throw error;
  }
  if (data === undefined) throw new Error("Empty response");
  return data;
}

// Auth

export async function sendMagicLink(
  email: string,
  ctx: "login" | "register",
  fullName?: string,
  redirectTo?: string,
  inviteToken?: string
): Promise<void> {
  await exec(
    postAuthSendMagicLink({
      client: getHttpClient(),
      body: {
        email,
        ctx,
        full_name: fullName,
        redirect_to: redirectTo,
        invite_token: inviteToken,
      },
    })
  );
}

export async function verifyMagicLink(
  challenge_id: string,
  token: string,
  ctx: string,
  name?: string,
  inviteToken?: string
): Promise<VerifyMagicLinkResponse> {
  return exec(
    postAuthVerifyMagicLink({
      client: getHttpClient(),
      body: { challenge_id, token, ctx, name, invite_token: inviteToken },
    })
  );
}

export async function verifyTotp(
  pending_token: string,
  code: string
): Promise<VerifyTotpSuccessResponse> {
  return exec(
    postAuthVerifyTotp({
      client: getHttpClient(),
      body: { pending_token, code },
    })
  );
}

export async function sendTrusteeLink(
  email: string,
  client_id: string
): Promise<void> {
  await exec(
    postAuthTrusteeSendLink({
      client: getHttpClient(),
      body: { email, client_id },
    })
  );
}

export async function verifyTrusteeLink(
  challenge_id: string,
  token: string,
  client_id: string
): Promise<VerifyTrusteeLinkResponse> {
  return exec(
    postAuthTrusteeVerifyLink({
      client: getHttpClient(),
      body: { challenge_id, token, client_id },
    })
  );
}

export async function getTotpStatus(): Promise<TotpStatusResponse> {
  return exec(
    getUserTotpStatus({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
}

export async function setupTotp(): Promise<SetupTotpResponse> {
  return exec(
    postUserSetupTotp({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
}

export async function confirmTotpSetup(code: string): Promise<void> {
  await exec(
    postUserConfirmTotpSetup({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { code },
    })
  );
}

export async function disableTotp(code: string): Promise<void> {
  await exec(
    postUserDisableTotp({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { code },
    })
  );
}

export async function getCurrentAuthUser(): Promise<AuthMeResponse> {
  return exec(
    getAuthMe({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  ) as Promise<AuthMeResponse>;
}

// Passkeys (WebAuthn)

export async function listPasskeys(): Promise<PasskeyListResponse> {
  return exec(
    getPasskeys({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
}

export async function startPasskeyRegistration(): Promise<PasskeyChallengeResponse> {
  return exec(
    postPasskeysRegisterStart({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
}

export async function finishPasskeyRegistration(
  challenge_id: string,
  credential: unknown,
  label?: string
): Promise<void> {
  await exec(
    postPasskeysRegisterFinish({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { challenge_id, credential, label },
    })
  );
}

export async function deletePasskey(credentialId: string): Promise<void> {
  await exec(
    deletePasskeysByCredentialId({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      url: `/passkeys/${encodeURIComponent(credentialId)}`,
    } as any)
  );
}

/** Begin a discoverable (usernameless) passkey login. Unauthenticated. */
export async function startPasskeyLogin(): Promise<PasskeyChallengeResponse> {
  return exec(
    postAuthPasskeyLoginStart({
      client: getHttpClient(),
    })
  );
}

/** Complete a passkey login. Returns the same envelope as verifyMagicLink. */
export async function finishPasskeyLogin(
  challenge_id: string,
  credential: unknown,
  redirect_to?: string
): Promise<VerifyMagicLinkResponse> {
  return exec(
    postAuthPasskeyLoginFinish({
      client: getHttpClient(),
      body: { challenge_id, credential, redirect_to },
    })
  );
}

export type {
  AttachmentInfo,
  MessageDoc,
  OrgInvitation,
  OrgListEntry,
  OrgMemberWithEmail,
  SetupTotpResponse,
  ThreadDetailResponse,
  ThreadDoc,
  ThreadsListResponse,
  TotpStatusResponse,
  VerifyMagicLinkResponse,
  VerifyTotpSuccessResponse,
} from "./client";

export async function listOrgs(): Promise<OrgListEntry[]> {
  const data = await exec(
    getOrgList({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    }) as any
  );
  return (data as any).orgs ?? [];
}

export async function createOrganisation(
  name: string
): Promise<CreateOrgResponse> {
  return exec(
    postOrg({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { name },
    }) as any
  ) as Promise<CreateOrgResponse>;
}

export async function deleteOrganisation(): Promise<void> {
  await exec(
    deleteOrg({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
}

export async function updateOrganisationName(name: string): Promise<void> {
  await exec(
    patchOrgName({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { name },
    })
  );
}

export async function getOrganisationMembers(): Promise<OrgMemberWithEmail[]> {
  const data = await exec(
    getOrgMembers({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
  return (data as any).members ?? [];
}

export async function getOrganisationMember(userId: string): Promise<any> {
  return exec(
    getOrgMembersByUserId({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      url: `/org/members/${encodeURIComponent(userId)}`,
    } as any)
  );
}

export async function deleteOrganisationMember(userId: string): Promise<void> {
  await exec(
    deleteOrgMembersByUserId({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      url: `/org/members/${encodeURIComponent(userId)}`,
    } as any)
  );
}

export async function updateOrganisationMemberRole(
  userId: string,
  role: string
): Promise<void> {
  await exec(
    patchOrgMembersByUserIdRole({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      url: `/org/members/${encodeURIComponent(userId)}/role`,
      body: { role },
    } as any)
  );
}

export async function getOrganisationInvites(): Promise<OrgInvitation[]> {
  const data = await exec(
    getOrgInvites({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
  return (data as any).invites ?? [];
}

export async function createOrganisationInvite(
  email: string,
  role: string
): Promise<void> {
  await exec(
    postOrgInvites({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { email, role },
    })
  );
}

export async function deleteOrganisationInvite(
  inviteId: string
): Promise<void> {
  await exec(
    deleteOrgInvitesByInviteId({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      url: `/org/invites/${encodeURIComponent(inviteId)}`,
    } as any)
  );
}

export async function acceptOrganisationInvite(
  inviteId: string
): Promise<AcceptInviteResponse> {
  return (await exec(
    postOrgInvitesByInviteIdAccept({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      url: `/org/invites/${encodeURIComponent(inviteId)}/accept`,
    } as any)
  )) as AcceptInviteResponse;
}

/**
 * Public — resolves an invite token to the org name, invited address and role for the
 * /join landing page. Deliberately does NOT send auth headers: the page must render for a
 * visitor who is not signed in, and a stale token header would only be noise.
 */
export async function previewInvite(
  token: string
): Promise<InvitePreviewResponse> {
  return (await exec(
    getOrgInvitesPreviewByToken({
      client: getHttpClient(),
      url: `/org/invites/preview/${encodeURIComponent(token)}`,
    } as any)
  )) as InvitePreviewResponse;
}

/** Accept an invitation by token — the path taken from the emailed /join link. */
export async function acceptInviteByToken(
  token: string
): Promise<AcceptInviteResponse> {
  return (await exec(
    postOrgInvitesAccept({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { token },
    } as any)
  )) as AcceptInviteResponse;
}

export async function rejectOrganisationInvite(
  inviteId: string
): Promise<void> {
  await exec(
    postOrgInvitesByInviteIdReject({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      url: `/org/invites/${encodeURIComponent(inviteId)}/reject`,
    } as any)
  );
}

export async function resendOrganisationInvite(
  inviteId: string
): Promise<void> {
  await exec(
    postOrgInvitesByInviteIdResend({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      url: `/org/invites/${encodeURIComponent(inviteId)}/resend`,
    } as any)
  );
}

export async function deleteCurrentAccountData(): Promise<void> {
  await exec(
    deleteAccountData({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
}

// Clients
export async function getClient(clientId: string) {
  return exec(
    getClientGet({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      query: { client_id: clientId },
    })
  );
}

export async function listClients(): Promise<ClientDoc[]> {
  const data = await exec(
    getClientList({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
  return data.clients;
}

export async function createClient(body: CreateClientRequest) {
  const data = await exec(
    postClientCreate({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body,
    })
  );
  return (data as any).id ?? data;
}

export async function updateClient(body: UpdateClientRequest) {
  await exec(
    postClientUpdate({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body,
    })
  );
}

/**
 * Vault-owner self-service rename. Writes the owner's own
 * `client.clients` first_name/last_name — the same columns the advisor client
 * list reads — authorized by the vault-project (`VaultIdentity`) token. Unlike
 * {@link updateClient} this needs no org context and cannot touch email/status.
 */
export async function updateOwnName(body: UpdateOwnNameRequest): Promise<void> {
  await exec(
    postVaultProfileName({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body,
    })
  );
}

// Vault deletion / reset.

/** Advisor-driven: wipe a client's vault contents, keep the client record. */
export async function resetClientVault(clientId: string): Promise<void> {
  await exec(
    postClientResetVault({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { client_id: clientId },
    })
  );
}

/** Vaults owned by the signed-in vault user (magic-link auth; works pre-unlock). */
export async function listOwnedVaults(): Promise<OwnedVaultItem[]> {
  const data = await exec(
    getRecoveryMyVaults({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
  return data.vaults;
}

/** Owner-driven (vault app): throw away the vault to recover from a lost password. */
export async function resetVault(
  clientId: string,
  confirmation: string
): Promise<ResetVaultResponse> {
  return exec(
    postRecoveryResetVault({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { client_id: clientId, confirmation },
    })
  );
}

export async function checkUserOwnsClient(clientId: string) {
  return exec(
    postClientCheckOwnership({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { clientId } as any,
    })
  );
}

export async function grantClientAccess(
  clientId: string,
  userId: string,
  role: string
): Promise<void> {
  await exec(
    postClientAccessGrant({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { client_id: clientId, user_id: userId, role } as any,
    })
  );
}

export async function revokeClientAccess(
  clientId: string,
  userId: string
): Promise<void> {
  await exec(
    postClientAccessRevoke({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { client_id: clientId, user_id: userId } as any,
    })
  );
}

export async function listClientAccess(clientId: string): Promise<any[]> {
  const data = await exec(
    getClientAccessList({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      query: { client_id: clientId } as any,
    } as any)
  );
  return (data as any).entries ?? [];
}

// Stripe / Billing
// Subscription Flow
export async function createSetupIntent(price_id: string, return_url: string) {
  return exec(
    postStripeCreateSubscriptionIntent({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { price_id, return_url },
    })
  );
}

export async function confirmSubscription(
  price_id: string,
  return_url: string,
  payment_method_id: string,
  seats?: number
) {
  return exec(
    postStripeConfirmSubscription({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { price_id, return_url, payment_method_id, seats },
    })
  );
}

export async function getCustomerPortal(return_url: string) {
  return exec(
    postStripeCustomerPortal({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { return_url },
    })
  );
}

export async function listUserInvoices(
  starting_after?: string
): Promise<InvoicesResponse> {
  const data = await exec(
    getStripeInvoices({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      query: { starting_after },
    }) as Promise<SdkResult<InvoicesResponse>>
  );
  return data as InvoicesResponse;
}

export async function checkSubscriptionStatus() {
  return exec(
    getStripeCheckSubscriptionStatus({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
}

export async function checkSubscriptionStatusInStripe() {
  return exec(
    getStripeCheckSubscriptionStatus({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      query: { force_refresh: true },
    })
  );
}

export async function revokeAllUserSessions() {
  await exec(
    postAuthRevokeSessions({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
}

// Password reset flows removed in favor of magic links

// User Settings
export async function getUser() {
  return exec(
    getUserApi({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
}

export async function updateUser(body: UpdateUserRequest) {
  return exec(
    patchUser({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body,
    })
  );
}

export type {
  ClientVaultDetails,
  ConfirmEmailChangeBody,
  CreateAttestationRequest,
  CreateAttestationResponse,
  RequestEmailChangeBody,
  RequestEmailChangeResponse,
  UserInviteItem,
};

export async function requestEmailChange(
  body: RequestEmailChangeBody
): Promise<RequestEmailChangeResponse> {
  return exec(
    postUserRequestEmailChange({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body,
    })
  );
}

export async function confirmEmailChange(
  body: ConfirmEmailChangeBody
): Promise<User> {
  return exec(
    postUserConfirmEmailChange({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body,
    })
  ) as Promise<User>;
}

export async function getUserInvites(): Promise<UserInviteItem[]> {
  const data = (await exec(
    getUserInvitesApi({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  )) as UserInviteListResponse;
  return data.invites;
}

export async function healthCheck() {
  return exec(
    getHealth({
      client: getHttpClient(),
    })
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Support Thread API
// ────────────────────────────────────────────────────────────────────────────

export interface CreateThreadResponse {
  thread_id: string;
  status: string;
  created_at: number;
}

// `POST /support/threads` is multipart (attachments), so it is not part of the
// generated OpenAPI SDK — the request shape is defined locally here.
export interface CreateThreadRequest {
  subject: string;
  category: string;
  message: string;
  attachments?: File[];
}

export async function createThread(
  data: CreateThreadRequest
): Promise<CreateThreadResponse> {
  const form = new FormData();
  form.append("subject", data.subject);
  form.append("category", data.category);
  form.append("message", data.message);
  if (data.attachments) {
    for (const file of data.attachments) {
      form.append("attachments", file);
    }
  }
  return exec(
    getHttpClient().post({
      url: "/support/threads",
      body: form,
      bodySerializer: (v) => v,
      headers: { ...(await withAuthHeaders()), "Content-Type": null },
    })
  ) as unknown as Promise<CreateThreadResponse>;
}

export async function listThreads(params?: {
  limit?: number;
  after?: string;
  status?: string;
}): Promise<ThreadsListResponse> {
  return exec(
    getSupportThreads({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      query: {
        limit: params?.limit,
        after: params?.after,
        status: params?.status,
      },
    })
  ) as Promise<ThreadsListResponse>;
}

export async function getThread(
  threadId: string,
  params?: { limit?: number; after?: string }
): Promise<ThreadDetailResponse> {
  return exec(
    getSupportThreadsByThreadId({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: { thread_id: threadId } as any,
      query: {
        limit: params?.limit,
        after: params?.after,
      },
    } as any)
  ) as Promise<ThreadDetailResponse>;
}

export async function addMessage(
  threadId: string,
  data: { message?: string; attachments?: File[] }
): Promise<MessageDoc> {
  const form = new FormData();
  if (data.message) form.append("message", data.message);
  if (data.attachments) {
    for (const file of data.attachments) {
      form.append("attachments", file);
    }
  }
  return exec(
    getHttpClient().post({
      url: `/support/threads/${threadId}/messages`,
      body: form,
      bodySerializer: (v) => v,
      headers: { ...(await withAuthHeaders()), "Content-Type": null },
    })
  ) as Promise<MessageDoc>;
}

export async function closeThread(threadId: string): Promise<void> {
  await exec(
    postSupportThreadsByThreadIdClose({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: { thread_id: threadId } as any,
      url: `/support/threads/${encodeURIComponent(threadId)}/close`,
    } as any)
  );
}

/**
 * Permanently delete one attachment from a message the caller authored.
 *
 * Attachments are addressed by their position in the message's `attachments`
 * array, because `gcs_path` is not unique — two files whose names sanitize to
 * the same string share one storage object. Pass the `gcsPath` you rendered as
 * a guard: the server returns 409 if it no longer matches the entry at `index`,
 * which catches a stale view deleting the wrong file.
 *
 * Resolves to the updated message (attachments omitted once the last one goes).
 */
export async function deleteSupportAttachment(
  threadId: string,
  messageId: string,
  index: number,
  gcsPath?: string
): Promise<MessageDoc> {
  return exec(
    deleteSupportThreadsByThreadIdMessagesByMessageIdAttachmentsByIndex({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: {
        thread_id: threadId,
        message_id: messageId,
        index,
      } as any,
      query: gcsPath ? { gcs_path: gcsPath } : undefined,
      url: `/support/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}/attachments/${index}`,
    } as any)
  ) as Promise<MessageDoc>;
}

// ────────────────────────────────────────────────────────────────────────────
// Vault OPAQUE Authentication API
// ────────────────────────────────────────────────────────────────────────────

export async function registerOpaqueStart(
  email: string,
  opaqueMessage: string
): Promise<RegisterInitResponse> {
  return exec(
    postClientAuthRegisterStart({
      client: getHttpClient(),
      body: { email, opaque_message: opaqueMessage },
    })
  );
}

export async function registerOpaqueFinish(
  attemptId: string,
  opaqueRecord: string,
  encryptedMkPw: string,
  encryptedMkRk?: string
): Promise<RegisterFinishResponse> {
  return exec(
    postClientAuthRegisterFinish({
      client: getHttpClient(),
      body: {
        attempt_id: attemptId,
        opaque_record: opaqueRecord,
        encrypted_mk_pw: encryptedMkPw,
        encrypted_mk_rk: encryptedMkRk,
      },
    })
  );
}

export async function loginOpaqueStart(
  email: string,
  opaqueKe1: string
): Promise<LoginInitResponse> {
  return exec(
    postClientAuthLoginStart({
      client: getHttpClient(),
      body: { email, opaque_ke1: opaqueKe1 },
    })
  );
}

export async function loginOpaqueFinish(
  attemptId: string,
  opaqueKe3: string
): Promise<LoginFinishResponse> {
  return exec(
    postClientAuthLoginFinish({
      client: getHttpClient(),
      body: { attempt_id: attemptId, opaque_ke3: opaqueKe3 },
    })
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Vault Files API (Secured under ClientAuth)
// ────────────────────────────────────────────────────────────────────────────

export async function listVaultFiles(
  clientId?: string
): Promise<FileListItem[]> {
  const data = await exec(
    getFilesList({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      query: { client_id: clientId } as any,
      cache: "no-store",
    })
  );
  return data.files || [];
}

export async function initiateVaultFile(
  body: InitiateFileRequest
): Promise<InitiateFileResponse> {
  return exec(
    postFilesInitiate({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      body,
    })
  );
}

export async function uploadVaultFile(
  fileId: string,
  encryptedBlob: Blob
): Promise<UploadResponse> {
  return exec(
    postFilesByFileIdUpload({
      client: getHttpClient(),
      headers: {
        ...(await withVaultHeaders()),
        "Content-Type": "application/octet-stream",
      },
      path: { file_id: fileId } as any,
      body: encryptedBlob,
      // The generated client only ever sends `serializedBody`; with
      // `bodySerializer: null` the raw body is dropped and an empty request
      // is sent (0-byte GCS object, broken download/preview). An identity
      // serializer makes the client attach the Blob unchanged.
      bodySerializer: (b: Blob) => b,
    } as any)
  );
}

export async function downloadVaultFile(fileId: string): Promise<Blob> {
  const data = await exec(
    getFilesByFileIdDownload({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      path: { file_id: fileId } as any,
      url: `/files/${encodeURIComponent(fileId)}/download`,
      parseAs: "blob",
    } as any)
  );
  return data as unknown as Blob;
}

export async function deleteVaultFile(fileId: string): Promise<void> {
  await exec(
    deleteFilesByFileId({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      path: { file_id: fileId } as any,
      url: `/files/${encodeURIComponent(fileId)}`,
    } as any)
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Trustee Key Shares API
// ────────────────────────────────────────────────────────────────────────────

export async function createTrusteeShare(
  body: CreateShareRequest
): Promise<CreateShareResponse> {
  return exec(
    postShares({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      body,
    })
  );
}

export async function listTrusteeShares(
  trusteeIdentifier: string
): Promise<ShareItem[]> {
  const data = await exec(
    getSharesByTrusteeByTrusteeIdentifier({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      path: { trustee_identifier: trusteeIdentifier } as any,
      url: `/shares/by-trustee/${encodeURIComponent(trusteeIdentifier)}`,
    } as any)
  );
  return data.shares || [];
}

export async function deleteTrusteeShare(shareId: string): Promise<void> {
  await exec(
    deleteSharesByShareId({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      path: { share_id: shareId } as any,
      url: `/shares/${encodeURIComponent(shareId)}`,
    } as any)
  );
}

/**
 * Designate a trustee share as the Executor. Persists the change server-side
 * (clearing any previous executor) independently of the Split & Distribute flow.
 */
export async function setTrusteeExecutor(shareId: string): Promise<void> {
  await exec(
    putSharesByShareIdExecutor({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      path: { share_id: shareId } as any,
      url: `/shares/${encodeURIComponent(shareId)}/executor`,
    } as any)
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Threshold-MPC Unlock API
// ────────────────────────────────────────────────────────────────────────────

/** Vault owner fetches their adviser's escrow public key to lock Trustee_Secrets. */
export async function getAdviserEscrowKey(): Promise<string | null> {
  const data = await exec(
    getUnlockAdviserKey({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
    }) as any
  );
  return (data as any)?.adviser_escrow_public_key ?? null;
}

/** Vault owner stores the ElGamal unlock ciphertext during trustee distribution. */
export async function setVaultUnlockConfig(
  body: SetVaultConfigRequest
): Promise<void> {
  await exec(
    postUnlockConfig({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      body,
    }) as any
  );
}

/** Vault owner reads back their stored unlock-config metadata (reconstruction threshold). */
export async function getVaultUnlockConfig(): Promise<GetVaultConfigResponse> {
  return exec(
    getUnlockConfig({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
    }) as any
  );
}

/** Adviser opens an unlock session after signing the death attestation. */
export async function startUnlockSession(
  body: StartUnlockRequest
): Promise<StartUnlockResponse> {
  return exec(
    postUnlockStart({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body,
    })
  );
}

/** Trustee submits its partial decryption to an unlock session. */
export async function submitUnlockPartial(
  sessionId: string,
  body: SubmitPartialRequest
): Promise<SubmitPartialResponse> {
  return exec(
    postUnlockBySessionIdPartial({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: { session_id: sessionId } as any,
      url: `/unlock/${encodeURIComponent(sessionId)}/partial`,
      body,
    } as any)
  );
}

/** Executor collects the opaque partials once the threshold is met. */
export async function listUnlockPartials(
  sessionId: string
): Promise<ListPartialsResponse> {
  return exec(
    getUnlockBySessionIdPartials({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: { session_id: sessionId } as any,
      url: `/unlock/${encodeURIComponent(sessionId)}/partials`,
    } as any)
  );
}

/** Trustee/executor polls unlock-session status. */
export async function getUnlockStatus(
  sessionId: string
): Promise<UnlockStatusResponse> {
  return exec(
    getUnlockBySessionId({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: { session_id: sessionId } as any,
      url: `/unlock/${encodeURIComponent(sessionId)}`,
    } as any)
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Trustee Identity Verification (KYC) API
// ────────────────────────────────────────────────────────────────────────────

/**
 * Start a trustee identity-verification session (Stripe Identity).
 *
 * Returns the provider `client_secret` the browser hands to Stripe's JS SDK to
 * collect the document + selfie. The outcome is written by the provider webhook —
 * the browser can never self-report success — so callers must poll
 * {@link getIdentityVerificationStatus} for the authoritative result.
 *
 * Uses vault-context headers (the caller is an authenticated trustee).
 */
export async function startIdentityVerification(
  body: StartVerificationRequest
): Promise<StartVerificationResponse> {
  return exec(
    postIdentityVerifyStart({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      body,
    })
  );
}

/**
 * Poll the caller's latest verification status for a vault + purpose.
 * `status` is one of 'pending' | 'verified' | 'failed' | 'expired' | 'none'.
 */
export async function getIdentityVerificationStatus(
  clientId: string,
  purpose: IdentityVerificationPurpose
): Promise<IdentityStatusResponse> {
  return exec(
    getIdentityVerifyStatus({
      client: getHttpClient(),
      headers: await withVaultHeaders(),
      query: { client_id: clientId, purpose },
    })
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Vault Client Metrics & Details API
// ────────────────────────────────────────────────────────────────────────────

export async function getClientVaultDetails(
  clientId: string
): Promise<ClientVaultDetails> {
  return exec(
    getClientByClientIdVaultDetails({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: { client_id: clientId } as any,
      url: `/client/${encodeURIComponent(clientId)}/vault-details`,
    } as any)
  ) as Promise<ClientVaultDetails>;
}

export async function postClientAttestation(
  clientId: string,
  body: CreateAttestationRequest
): Promise<CreateAttestationResponse> {
  return exec(
    postClientByClientIdAttestation({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: { client_id: clientId } as any,
      url: `/client/${encodeURIComponent(clientId)}/attestation`,
      body,
    } as any)
  ) as Promise<CreateAttestationResponse>;
}

// ────────────────────────────────────────────────────────────────────────────
// Escrow Public Key API
// ────────────────────────────────────────────────────────────────────────────

export async function postOrgEscrowKey(publicKey: string): Promise<void> {
  await exec(
    postOrgEscrowKeyApi({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body: { public_key: publicKey },
    })
  );
}

// ────────────────────────────────────────────────────────────────────────────
// White-Label Branding API
// ────────────────────────────────────────────────────────────────────────────

export async function getOrgBranding(): Promise<GetBrandingResponse> {
  return exec(
    getOrgBrandingApi({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  ) as Promise<GetBrandingResponse>;
}

export async function patchOrgBranding(
  body: UpdateBrandingRequest
): Promise<void> {
  await exec(
    patchOrgBrandingApi({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      body,
    })
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Admin Firms API
// ────────────────────────────────────────────────────────────────────────────

export async function getAdminFirmsList(): Promise<any[]> {
  const res = await exec(
    getAdminFirms({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
    })
  );
  return res.firms;
}

// ────────────────────────────────────────────────────────────────────────────
// Unlock Petitions API (Multipart/direct fetch)
//
// A trustee uploads a death certificate to request a vault unlock; an advisor with
// client access reviews the decrypted document and confirms it via the attestation
// ceremony. There is no platform approval.
// ────────────────────────────────────────────────────────────────────────────

export type UnlockPetitionItem = {
  petition_id: string;
  client_id: string;
  org_id: string;
  petitioner_email: string;
  death_certificate_object_key: string;
  death_certificate_mime?: string | null;
  death_certificate_encrypted?: boolean;
  status: string; // pending | confirmed | rejected | cancelled
  created_at: number;
  updated_at: number;
};

/**
 * Fetch the vault's petition-document public key (P-256, SEC1 uncompressed, base64). The
 * caller (a trustee) encrypts the petition document to this key with `encryptECIES`
 * before uploading, so the server never sees plaintext.
 */
export async function getVaultPetitionDocKey(
  clientId: string
): Promise<string> {
  const data = await exec(
    getUnlockPetitionsPetitionDocKeyByClientId({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: { client_id: clientId } as any,
      url: `/unlock-petitions/petition-doc-key/${encodeURIComponent(clientId)}`,
    } as any)
  );
  return (data as PetitionDocKeyResponse).public_key;
}

/**
 * Submit an unlock petition (a trustee's request to unlock a vault, backed by a death
 * certificate). The document must already be ECIES-encrypted to the vault's petition-document
 * public key (see `getVaultPetitionDocKey`); `documentMime` is the plaintext document's
 * MIME type, retained so the advisor can view it after decryption.
 */
export async function createUnlockPetition(
  clientId: string,
  petitionerEmail: string,
  encryptedDocument: Uint8Array,
  documentMime: string
): Promise<{ petition_id: string }> {
  const formData = new FormData();
  formData.append("client_id", clientId);
  formData.append("petitioner_email", petitionerEmail);
  formData.append(
    "certificate_mime",
    documentMime || "application/octet-stream"
  );
  // The blob is opaque ciphertext; its own content-type is irrelevant. Copy into a
  // fresh ArrayBuffer to satisfy the BlobPart type across TS lib targets.
  const buffer = encryptedDocument.slice().buffer as ArrayBuffer;
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  formData.append("death_certificate", blob, "petition.enc");

  // POST /unlock-petitions is a multipart route registered with plain `.route()`
  // (multipart doesn't implement aide's OperationInput), so it isn't in the
  // OpenAPI spec and has no generated per-operation wrapper. Route it through
  // the generated client instance instead — same request pipeline (auth
  // interceptors, base URL), just untyped — mirroring `addMessage`.
  return exec(
    getHttpClient().post({
      url: "/unlock-petitions",
      body: formData,
      bodySerializer: (v) => v,
      headers: { ...(await withAuthHeaders()), "Content-Type": null },
    })
  ) as Promise<{ petition_id: string }>;
}

/**
 * Advisory download: fetch an unlock petition's decrypted document as a Blob. Gated on
 * the caller having client-level access to the vault's owning org (server-side).
 */
export async function getUnlockPetitionCertificate(
  petitionId: string
): Promise<Blob> {
  const data = await exec(
    getUnlockPetitionsByPetitionIdCertificate({
      client: getHttpClient(),
      headers: await withAuthHeaders(),
      path: { petition_id: petitionId } as any,
      url: `/unlock-petitions/${encodeURIComponent(petitionId)}/certificate`,
      parseAs: "blob",
    } as any)
  );
  return data as unknown as Blob;
}

export async function listTrusteeUnlockPetitions(): Promise<
  UnlockPetitionItem[]
> {
  // GET /unlock-petitions shares a plain `.route()` registration with the multipart
  // POST /unlock-petitions, so it isn't in the OpenAPI spec and has no generated
  // per-operation wrapper. Route it through the generated client instance.
  const data = await exec(
    getHttpClient().get({
      url: "/unlock-petitions",
      headers: await withAuthHeaders(),
    }) as any
  );
  return (data as any).petitions || [];
}

export type { ShareItem };
